// CRM integration (admin): health of the tenant's CRM connections, the connections themselves (add, authorize with
// the CRM's own sign-in page, test, revoke, remove) and the tenant's own OAuth clients. Settings, mappings and the
// sync activity of one connection are on its own page (ConnectionPage). The browser never sees a token or a client
// secret: authorizing sends the user to the CRM, which returns to the API's callback, which returns here.
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, KeyRound, PlugZap, Unplug, Trash2, Pencil, ExternalLink, Activity } from 'lucide-react';
import { PageHeader, DataTable, ErrorBox, Field, Stat } from '@/components/common';
import { Button } from '@/components/ui/button';
import { Input, Select, Checkbox } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { StatusBadge, Badge } from '@/components/ui/badge';
import { toast } from '@/store/toast';
import { useCan } from '@/store/auth';
import { fmtTime } from '@/lib/utils';
import { t } from '@/i18n';
import { crmApi, crmErrorText, type Connection, type OAuthClient, type Provider } from './types';

const ZOHO_DCS = ['us', 'eu', 'in', 'au', 'jp', 'ca', 'cn', 'sa'];

interface Health {
  jobs: Record<string, number>; queue_lag_sec: number; activities_pending: number; activities_oldest_pending: string | null; open_conflicts: number;
}

export function useProviders() {
  return useQuery({ queryKey: ['crm-providers'], queryFn: () => crmApi<{ providers: Provider[] }>('/providers'), staleTime: 300_000 });
}

/** sends the browser to the CRM's sign-in page; the API's callback brings it back to /admin/crm */
export async function authorize(c: Pick<Connection, 'id'>): Promise<void> {
  try {
    const r = await crmApi<{ url: string }>(`/connections/${c.id}/authorize`, { method: 'POST', body: {} });
    window.location.assign(r.url);
  } catch (e) { toast.error(crmErrorText(e)); }
}

export function CrmPage() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const conns = useQuery({ queryKey: ['crm-connections'], queryFn: () => crmApi<{ connections: Connection[] }>('/connections') });
  const health = useQuery({ queryKey: ['crm-health'], queryFn: () => crmApi<Health>('/health'), refetchInterval: 30_000 });
  const providers = useProviders();
  const canEdit = useCan('crm.edit'), canAuth = useCan('crm.authorize');
  const [adding, setAdding] = React.useState(false);
  const refresh = () => { conns.refetch(); health.refetch(); };

  // back from the CRM's sign-in page
  React.useEffect(() => {
    const result = params.get('crm_result');
    if (!result) return;
    if (result === 'connected') toast.ok(t('The CRM connection is authorized.'));
    else toast.error(t('Authorization failed: {{code}}', { code: params.get('code') ?? '?' }));
    const id = params.get('crm_connection');
    setParams({}, { replace: true });
    if (result === 'connected' && id) nav(`/admin/crm/${id}`);
  }, [params, setParams, nav]);

  async function act(c: Connection, what: 'test' | 'revoke' | 'delete') {
    if (what === 'revoke' && !confirm(t('Revoke access to {{name}}? Calls stop being logged until it is authorized again.', { name: c.name }))) return;
    if (what === 'delete' && !confirm(t('Remove {{name}} and everything synced through it?', { name: c.name }))) return;
    try {
      if (what === 'test') {
        const r = await crmApi<{ connection: Connection }>(`/connections/${c.id}/test`, { method: 'POST', body: {} });
        toast.ok(t('{{name}} answers.', { name: r.connection.name }));
      } else if (what === 'revoke') { await crmApi(`/connections/${c.id}/revoke`, { method: 'POST', body: {} }); toast.ok(t('Access revoked')); }
      else { await crmApi(`/connections/${c.id}`, { method: 'DELETE' }); toast.ok(t('Removed')); }
    } catch (e) { toast.error(crmErrorText(e)); }
    refresh();
  }

  const h = health.data;
  return (
    <div className="grid gap-4">
      <PageHeader title={t('CRM integration')} description={t('Connect Zoho CRM or Salesforce: screen pop for agents, calls and dispositions logged in the CRM, contacts synced for the dialer.')}>
        {canEdit && <Button onClick={() => setAdding(true)}><Plus /> {t('Add connection')}</Button>}
      </PageHeader>
      <ErrorBox error={conns.error} />
      {h && <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="crm-health">
        <Stat label={t('Jobs waiting')} value={(h.jobs.PENDING ?? 0) + (h.jobs.RETRY ?? 0)} hint={h.queue_lag_sec ? t('oldest due {{n}} s ago', { n: h.queue_lag_sec }) : undefined} />
        <Stat label={t('Failed jobs')} value={h.jobs.DEAD ?? 0} hint={t('Retry them on the connection page.')} />
        <Stat label={t('Calls not logged yet')} value={h.activities_pending} hint={h.activities_oldest_pending ? t('since {{time}}', { time: fmtTime(h.activities_oldest_pending) }) : undefined} />
        <Stat label={t('Sync conflicts')} value={h.open_conflicts} />
      </div>}
      <Card>
        <CardHeader><CardTitle>{t('Connections')}</CardTitle></CardHeader>
        <CardContent>
          <DataTable rowKey={(r) => r.id} rows={conns.data?.connections} empty={t('No CRM connected yet.')} onRowClick={(r) => nav(`/admin/crm/${r.id}`)} columns={[
            { key: 'name', label: t('Name'), render: (r) => <span className="font-medium">{r.name}{r.is_default_lookup && <Badge variant="info" className="ml-2">{t('screen pop')}</Badge>}</span> },
            { key: 'provider', label: t('CRM'), render: (r) => `${r.provider === 'ZOHO' ? 'Zoho CRM' : 'Salesforce'}${r.environment !== 'PRODUCTION' ? ` · ${t(r.environment === 'SANDBOX' ? 'Sandbox' : 'Developer')}` : ''}` },
            { key: 'status', label: t('Status'), render: (r) => <span className="grid gap-0.5"><StatusBadge value={r.enabled ? r.status : 'DISABLED'} />
              {r.paused_until && <span className="text-xs text-warning">{t('paused until {{time}}', { time: fmtTime(r.paused_until) })}</span>}</span> },
            { key: 'last_success_at', label: t('Last success'), render: (r) => (r.last_success_at ? fmtTime(r.last_success_at) : '-') },
            { key: 'last_error', label: t('Last error'), render: (r) => r.last_error.code
              ? <span className="text-xs text-destructive" title={r.last_error.message ?? ''}>{r.last_error.code} · {fmtTime(r.last_error.at)}</span> : '-' }]}
            actions={(r) => <>
              {canAuth && <Button size="sm" variant={r.authorized ? 'outline' : 'default'} onClick={() => authorize(r)}><KeyRound /> {r.authorized ? t('Re-authorize') : t('Authorize')}</Button>}
              {r.authorized && <Button size="icon" variant="ghost" aria-label={t('Test {{name}}', { name: r.name })} onClick={() => act(r, 'test')}><PlugZap /></Button>}
              {canAuth && r.authorized && <Button size="icon" variant="ghost" aria-label={t('Revoke {{name}}', { name: r.name })} onClick={() => act(r, 'revoke')}><Unplug /></Button>}
              <Button size="icon" variant="ghost" aria-label={t('Open {{name}}', { name: r.name })} onClick={() => nav(`/admin/crm/${r.id}`)}><Activity /></Button>
              {canEdit && <Button size="icon" variant="ghost" aria-label={t('Remove {{name}}', { name: r.name })} onClick={() => act(r, 'delete')}><Trash2 className="text-destructive" /></Button>}
            </>} />
        </CardContent>
      </Card>
      <OAuthClients providers={providers.data?.providers ?? []} />
      <Dialog open={adding} onOpenChange={setAdding}>
        {adding && <DialogContent title={t('Add connection')} className="max-w-lg">
          <NewConnection providers={providers.data?.providers ?? []} onDone={(id) => { setAdding(false); refresh(); if (id) nav(`/admin/crm/${id}`); }} />
        </DialogContent>}
      </Dialog>
    </div>
  );
}

function NewConnection({ providers, onDone }: { providers: Provider[]; onDone: (id?: number) => void }) {
  const clients = useQuery({ queryKey: ['crm-clients'], queryFn: () => crmApi<{ clients: OAuthClient[] }>('/oauth-clients') });
  const [v, setV] = React.useState({ provider: providers[0]?.code ?? 'ZOHO', name: '', environment: 'PRODUCTION', data_center: 'us', client: '', country: '' });
  const [error, setError] = React.useState<unknown>(null);
  const set = (k: keyof typeof v) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setV((x) => ({ ...x, [k]: e.target.value }));
  const p = providers.find((x) => x.code === v.provider);
  const own = (clients.data?.clients ?? []).filter((c) => c.provider === v.provider && c.enabled);
  async function save(e: React.FormEvent) {
    e.preventDefault(); setError(null);
    const body: Record<string, unknown> = { provider: v.provider, name: v.name, environment: v.environment, oauth_client_id: v.client ? Number(v.client) : null };
    if (v.provider === 'ZOHO') body.data_center = v.data_center;
    if (v.country) body.settings = { country: v.country.toUpperCase() };
    try {
      const r = await crmApi<{ connection: Connection }>('/connections', { body });
      toast.ok(t('Saved. Authorize it to finish.'));
      onDone(r.connection.id);
    } catch (err) { setError(new Error(crmErrorText(err))); }
  }
  return (
    <form onSubmit={save} className="grid gap-3">
      <Field help="crm.provider" label={t('CRM')}><Select name="provider" value={v.provider} onChange={set('provider')}>
        {providers.map((x) => <option key={x.code} value={x.code} disabled={!x.enabled}>{x.name}</option>)}
      </Select></Field>
      <Field help="crm.name" label={t('Name')}><Input name="name" value={v.name} onChange={set('name')} maxLength={100} required placeholder={p?.name} /></Field>
      {v.provider === 'ZOHO'
        ? <Field help="crm.data_center" label={t('Data center')} hint={t('Where the Zoho account lives (the domain you sign in at).')}>
            <Select name="data_center" value={v.data_center} onChange={set('data_center')}>{ZOHO_DCS.map((d) => <option key={d} value={d}>{d.toUpperCase()}</option>)}</Select>
          </Field>
        : <Field help="crm.environment" label={t('Org type')}><Select name="environment" value={v.environment} onChange={set('environment')}>
            <option value="PRODUCTION">{t('Production')}</option><option value="SANDBOX">{t('Sandbox')}</option><option value="DEVELOPER">{t('Developer')}</option>
          </Select></Field>}
      <Field help="crm.oauth_client" label={t('OAuth client')} hint={p && !p.platform_client && !own.length ? t('No platform client is configured for this CRM: add your own below first.') : undefined}>
        <Select name="oauth_client_id" value={v.client} onChange={set('client')}>
          {p?.platform_client && <option value="">{t('Platform client')}</option>}
          {!p?.platform_client && <option value="">{t('Choose…')}</option>}
          {own.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
      </Field>
      <Field help="crm.country" label={t('Default country')} hint={t('Two letters, for phone numbers stored without a country code. Empty: the tenant default.')}>
        <Input name="country" value={v.country} onChange={set('country')} maxLength={2} placeholder="US" />
      </Field>
      <ErrorBox error={error} />
      <div className="flex justify-end"><Button type="submit">{t('Save')}</Button></div>
    </form>
  );
}

function OAuthClients({ providers }: { providers: Provider[] }) {
  const q = useQuery({ queryKey: ['crm-clients'], queryFn: () => crmApi<{ clients: OAuthClient[] }>('/oauth-clients') });
  const canAuth = useCan('crm.authorize');
  const [editing, setEditing] = React.useState<OAuthClient | 'new' | null>(null);
  async function remove(c: OAuthClient) {
    if (!confirm(t('Remove the OAuth client {{name}}?', { name: c.name }))) return;
    try { await crmApi(`/oauth-clients/${c.id}`, { method: 'DELETE' }); toast.ok(t('Removed')); q.refetch(); } catch (e) { toast.error(crmErrorText(e)); }
  }
  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div><CardTitle>{t('Your OAuth clients')}</CardTitle>
          <CardDescription>{t('Optional: your own connected app (Salesforce) or API client (Zoho) instead of the platform one. The secret is stored encrypted and never shown again.')}</CardDescription></div>
        {canAuth && <Button variant="outline" onClick={() => setEditing('new')}><Plus /> {t('Add client')}</Button>}
      </CardHeader>
      <CardContent>
        <DataTable rowKey={(r) => r.id} rows={q.data?.clients} empty={t('None: connections use the platform client.')} columns={[
          { key: 'name', label: t('Name'), render: (r) => <span className="font-medium">{r.name}</span> },
          { key: 'provider', label: t('CRM'), render: (r) => providers.find((p) => p.code === r.provider)?.name ?? r.provider },
          { key: 'client_id', label: t('Client ID'), render: (r) => <code className="text-xs">{r.client_id}</code> },
          { key: 'redirect_uri', label: t('Redirect URI'), render: (r) => <code className="break-all text-xs">{r.redirect_uri}</code> },
          { key: 'has_secret', label: t('Secret'), render: (r) => (r.has_secret ? t('set') : t('none (PKCE only)')) },
          { key: 'connections', label: t('Used by') },
          { key: 'enabled', label: t('Status'), render: (r) => <StatusBadge value={r.enabled ? 'ACTIVE' : 'DISABLED'} /> }]}
          actions={(r) => canAuth ? <>
            <Button size="icon" variant="ghost" aria-label={t('Edit {{name}}', { name: r.name })} onClick={() => setEditing(r)}><Pencil /></Button>
            <Button size="icon" variant="ghost" aria-label={t('Remove {{name}}', { name: r.name })} onClick={() => remove(r)}><Trash2 className="text-destructive" /></Button>
          </> : null} />
        {providers.some((p) => p.callback_url) && <p className="mt-3 flex items-center gap-1 text-xs text-muted-foreground">
          <ExternalLink className="h-3 w-3" /> {t('Register the redirect URI shown above as the callback URL of your app in the CRM.')}</p>}
      </CardContent>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        {editing && <DialogContent title={editing === 'new' ? t('Add client') : t('Edit {{name}}', { name: editing.name })} className="max-w-lg">
          <ClientForm client={editing === 'new' ? null : editing} providers={providers} onDone={() => { setEditing(null); q.refetch(); }} />
        </DialogContent>}
      </Dialog>
    </Card>
  );
}

function ClientForm({ client, providers, onDone }: { client: OAuthClient | null; providers: Provider[]; onDone: () => void }) {
  const [v, setV] = React.useState({ provider: client?.provider ?? providers[0]?.code ?? 'ZOHO', name: client?.name ?? '', client_id: client?.client_id ?? '',
                                     secret: '', scopes: client?.scopes ?? '', use_pkce: client?.use_pkce ?? true, enabled: client?.enabled ?? true });
  const [error, setError] = React.useState<unknown>(null);
  const set = (k: keyof typeof v) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setV((x) => ({ ...x, [k]: e.target.value }));
  async function save(e: React.FormEvent) {
    e.preventDefault(); setError(null);
    try {
      if (client) {
        const body: Record<string, unknown> = { name: v.name, use_pkce: v.use_pkce, enabled: v.enabled };
        if (v.scopes && v.scopes !== client.scopes) body.scopes = v.scopes;
        if (v.secret) body.client_secret = v.secret;
        await crmApi(`/oauth-clients/${client.id}`, { method: 'PATCH', body });
      } else {
        const body: Record<string, unknown> = { provider: v.provider, name: v.name, client_id: v.client_id.trim(), use_pkce: v.use_pkce };
        if (v.secret) body.client_secret = v.secret;
        if (v.scopes.trim()) body.scopes = v.scopes.trim();
        await crmApi('/oauth-clients', { body });
      }
      toast.ok(t('Saved')); onDone();
    } catch (err) { setError(new Error(crmErrorText(err))); }
  }
  return (
    <form onSubmit={save} className="grid gap-3">
      {!client && <Field help="crmclient.provider" label={t('CRM')}><Select name="provider" value={v.provider} onChange={set('provider')}>{providers.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}</Select></Field>}
      <Field help="crmclient.name" label={t('Name')}><Input name="name" value={v.name} onChange={set('name')} maxLength={100} required /></Field>
      <Field help="crmclient.client_id" label={t('Client ID')}><Input name="client_id" value={v.client_id} onChange={set('client_id')} disabled={!!client} required autoComplete="off" /></Field>
      <Field help="crmclient.client_secret" label={t('Client secret')} hint={client?.has_secret ? t('Set. Type a new one to replace it.') : t('Leave empty for a PKCE-only client.')}>
        <Input name="client_secret" type="password" autoComplete="new-password" value={v.secret} onChange={set('secret')} maxLength={512} />
      </Field>
      <Field help="crmclient.scopes" label={t('Scopes')} hint={t('Space separated. Empty: the scopes this module needs.')}><Input name="scopes" value={v.scopes} onChange={set('scopes')} /></Field>
      <label className="flex items-center gap-2 text-sm"><Checkbox checked={v.use_pkce} onChange={(e) => setV((x) => ({ ...x, use_pkce: e.target.checked }))} /> {t('Use PKCE')}</label>
      {client && <label className="flex items-center gap-2 text-sm"><Checkbox checked={v.enabled} onChange={(e) => setV((x) => ({ ...x, enabled: e.target.checked }))} /> {t('Enabled')}</label>}
      <ErrorBox error={error} />
      <div className="flex justify-end"><Button type="submit">{t('Save')}</Button></div>
    </form>
  );
}
